import { Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { CommonModule } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { Navbar } from '../navbar/navbar';
import { Subbar } from '../subbar/subbar';
import { Footer } from '../footer/footer';
import { NotificacionToast } from '../../../shared/components/notificacion-toast/notificacion-toast';
import { PermisosService } from '../../../core/services/permisos.service';
import { InactivityService } from '../../../core/services/inactivity.service';
import { AvisoService } from '../../../core/navegacion/aviso.service';
import { ObraGlobalService } from '../../../core/navegacion/obra-global.service';
import { rutaDe } from '../../../core/navegacion/registro';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [CommonModule, RouterOutlet, Navbar, Subbar, Footer, NotificacionToast, DialogModule, ConfirmDialogModule],
  templateUrl: './main-layout.html',
  styleUrl: './main-layout.scss',
})
export class MainLayout implements OnInit, OnDestroy {
  permisosSvc    = inject(PermisosService);
  inactivitySvc  = inject(InactivityService);
  avisoSvc       = inject(AvisoService);
  /** Su `llave` sube cuando la persona cambia de obra con el selector: la pantalla se vuelve a armar y carga los datos de la obra nueva. */
  obraGlobal     = inject(ObraGlobalService);

  private router = inject(Router);

  constructor() {
    // Al cambiar de pantalla el foco se va al contenido (si no, un lector de pantalla o el teclado se quedan en el enlace que se pulsó, que ya no existe).
    // Se salta la primera carga (ahí el foco natural es el de la página), no se le quita el foco a quien está escribiendo en un campo y NO se mueve cuando solo
    // cambia la consulta de la misma pantalla (página, orden, filtro de una lista: el botón que la persona pulsó sigue ahí y el foco debe quedarse en él).
    let anterior: string | null = null;
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd), takeUntilDestroyed(inject(DestroyRef))).subscribe(e => {
      const ruta = rutaDe(e.urlAfterRedirects);
      const habia = anterior;
      anterior = ruta;
      if (habia === null || habia === ruta) return;
      setTimeout(() => {
        const activo = document.activeElement as HTMLElement | null;
        if (activo && /^(INPUT|TEXTAREA|SELECT)$/.test(activo.tagName) && activo.closest('main')) return;
        document.getElementById('contenido-principal')?.focus({ preventScroll: true });
      }, 0);
    });

    // Un diálogo se abre siempre desde su primera línea. PrimeNG enfoca el primer control del cuerpo y el navegador desplaza hasta él: en un detalle cuyo primer
    // botón o enlace está al final (un contrato, una solicitud) el diálogo se abría casi al fondo y escondía el estado y los datos de arriba. Solo se corrige
    // la PRIMERA vez que entra el foco en cada cuerpo de diálogo (el foco inicial de PrimeNG); después no se toca el scroll de nadie.
    // Si ese primer foco lo provoca la propia persona (un clic, un toque o Tab sobre un control del cuerpo ya desplazado, porque el foco inicial cayó en la cabecera
    // o en el pie), NO se reinicia: el cuerpo saltaría al inicio entre `mousedown` y `mouseup`, el control se alejaría del puntero y el clic se perdería.
    // El foco de un gesto llega en el mismo ciclo que su `mousedown` o `keydown`; el foco inicial de PrimeNG llega después.
    const cuerposVistos = new WeakSet<Element>();
    let gestoEnCurso = false;
    const alGesto = (): void => { gestoEnCurso = true; setTimeout(() => { gestoEnCurso = false; }, 0); };
    const alEnfocar = (ev: FocusEvent): void => {
      const cuerpo = (ev.target as Element | null)?.closest?.('.p-dialog-content') as HTMLElement | null;
      if (!cuerpo || cuerposVistos.has(cuerpo)) return;
      cuerposVistos.add(cuerpo);
      if (gestoEnCurso) return;
      // Se espera a que termine el desplazamiento que provoca el propio foco (microtarea: corre antes de pintar y después de `focus()`).
      queueMicrotask(() => { if (cuerpo.scrollTop > 0) cuerpo.scrollTop = 0; });
    };
    document.addEventListener('mousedown', alGesto, true);
    document.addEventListener('keydown', alGesto, true);
    document.addEventListener('focusin', alEnfocar);
    inject(DestroyRef).onDestroy(() => {
      document.removeEventListener('mousedown', alGesto, true);
      document.removeEventListener('keydown', alGesto, true);
      document.removeEventListener('focusin', alEnfocar);
    });
  }

  saltarAlContenido(ev: Event): void {
    ev.preventDefault();
    const main = document.getElementById('contenido-principal');
    main?.focus();
    main?.scrollIntoView({ block: 'start' });
  }

  ngOnInit(): void {
    this.inactivitySvc.start();
  }

  ngOnDestroy(): void {
    this.inactivitySvc.stop();
  }
}
